import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { DEMO } from "@/lib/content";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { motion } from "framer-motion";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { submitForm, SubmitFormError } from "@/lib/submit-form";
import { track } from "@/lib/analytics";
import { isPersonalEmail, WORK_EMAIL_MESSAGE } from "@/lib/email-validation";

const demoSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z
    .string()
    .email("Valid work email is required")
    .refine((v) => !isPersonalEmail(v), WORK_EMAIL_MESSAGE),
  company: z.string().min(2, "Company name is required"),
  role: z.string().min(2, "Job title is required"),
  inquiryType: z.string().min(1, "Please select what you'd like to discuss"),
  message: z.string().min(10, "Please share a few sentences about your contracts or workflow"),
});

export default function DemoPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<z.infer<typeof demoSchema>>({
    resolver: zodResolver(demoSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      company: "",
      role: "",
      inquiryType: "",
      message: "",
    },
  });

  async function onSubmit(values: z.infer<typeof demoSchema>) {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await submitForm("demo", values);
      track("marketing_form_submitted", {
        source: "demo",
        company: values.company,
      });
      setIsSuccess(true);
    } catch (err) {
      if (err instanceof SubmitFormError && err.fieldErrors) {
        for (const [name, message] of Object.entries(err.fieldErrors)) {
          form.setError(name as keyof z.infer<typeof demoSchema>, {
            type: "server",
            message,
          });
        }
      }
      setSubmitError(
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Layout>
      <SEOHead title={DEMO.seo.title} description={DEMO.seo.description} />
      
      <section className="pt-24 pb-32 md:pt-32 bg-slate-50 dark:bg-navy/30 min-h-[calc(100vh-64px)]">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center max-w-6xl mx-auto">
            
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 mb-4 tracking-wider uppercase">
                {DEMO.eyebrow}
              </div>
              <h1 className="text-display mb-6 text-navy dark:text-white">
                {DEMO.title}
              </h1>
              <p className="text-xl text-slate-600 dark:text-slate-300 mb-12 leading-relaxed">
                {DEMO.subtitle}
              </p>

              <div className="space-y-6 mb-12">
                <h3 className="font-semibold text-foreground">What to expect:</h3>
                <ul className="space-y-4">
                  {DEMO.bullets.map((item: string, i: number) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-cyan-500 shrink-0 mt-0.5" />
                      <span className="text-slate-600 dark:text-slate-400">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="bg-card border rounded-3xl p-8 md:p-10 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 inset-x-0 h-1 bg-brand-gradient" />
                
                {isSuccess ? (
                  <div className="text-center py-12">
                    <div className="h-16 w-16 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 mb-6">
                      <CheckCircle2 className="h-8 w-8" />
                    </div>
                    <h3 className="text-2xl font-bold mb-4">Request received</h3>
                    <p className="text-muted-foreground mb-8">
                      Thank you for your interest. A solutions engineer will be in touch shortly to schedule your demo.
                    </p>
                  </div>
                ) : (
                  <>
                    <h3 className="text-2xl font-bold mb-8">Schedule your session</h3>
                    <Form {...form}>
                      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          <FormField
                            control={form.control}
                            name="firstName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>First Name</FormLabel>
                                <FormControl>
                                  <Input placeholder="Jane" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="lastName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Last Name</FormLabel>
                                <FormControl>
                                  <Input placeholder="Doe" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Work Email</FormLabel>
                              <FormControl>
                                <Input placeholder="jane@company.com" type="email" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="company"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Company Name</FormLabel>
                              <FormControl>
                                <Input placeholder="Acme Corp" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="role"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Job Title</FormLabel>
                              <FormControl>
                                <Input placeholder="General Counsel" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="inquiryType"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>What would you like to discuss?</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select a focus area" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="nda-turnaround">NDA & MSA turnaround</SelectItem>
                                  <SelectItem value="playbook-enforcement">Playbook enforcement</SelectItem>
                                  <SelectItem value="repository-search">Repository & clause search</SelectItem>
                                  <SelectItem value="renewals-obligations">Renewals & obligations</SelectItem>
                                  <SelectItem value="general-platform">General platform walkthrough</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="message"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Tell us about your contracts</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="What contract types, volume, and pain points should we focus on?"
                                  rows={4}
                                  className="resize-y"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {submitError && (
                          <div
                            role="alert"
                            data-testid="demo-submit-error"
                            className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-400"
                          >
                            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                            <span>{submitError}</span>
                          </div>
                        )}

                        <div className="pt-4">
                          <Button type="submit" className="w-full h-12 text-base font-semibold bg-brand-gradient text-white border-0 hover:opacity-90" disabled={isSubmitting}>
                            {isSubmitting ? "Submitting..." : "Request Demo"}
                          </Button>
                        </div>
                        <p className="text-xs text-center text-muted-foreground mt-4">
                          By submitting this form, you agree to our Privacy Policy and Terms of Service.
                        </p>
                      </form>
                    </Form>
                  </>
                )}
              </div>
            </motion.div>

          </div>
        </div>
      </section>
    </Layout>
  );
}
